import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "@/lib/business";

const NAV = [
    { href: "#inicio", label: "Inicio" },
    { href: "#servicios", label: "Servicios" },
    { href: "#nosotros", label: "Nosotros" },
    { href: "#galeria", label: "Galería" },
    { href: "#contacto", label: "Contacto" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled || open
                    ? "bg-brand-cream/95 backdrop-blur-md border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.03)]"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="flex items-center justify-between h-[72px]">
                    <Link
                        to="/"
                        data-testid="header-logo-link"
                        className="flex items-center gap-3 group"
                        onClick={() => setOpen(false)}
                    >
                        <div className="h-11 w-11 rounded-xl bg-brand-navy grid place-items-center overflow-hidden ring-1 ring-black/5">
                            <img
                                src="/assets/logo.png"
                                alt="Fontanería Rodrigo"
                                className="h-10 w-10 object-contain"
                            />
                        </div>
                        <div className="leading-tight hidden sm:block">
                            <div className={`font-display font-bold text-[17px] tracking-tight transition-colors ${
                                scrolled || open ? "text-brand-navy" : "text-white"
                            }`}>
                                Fontanería Rodrigo
                            </div>
                            <div className={`text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors ${
                                scrolled || open ? "text-brand-blue-700" : "text-white/70"
                            }`}>
                                Fontanería y calefacción
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-9">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                data-testid={`nav-${n.label.toLowerCase()}`}
                                className={`link-underline text-[14px] font-medium transition-colors ${
                                    scrolled || open
                                        ? "text-brand-ink/80 hover:text-brand-navy"
                                        : "text-white/85 hover:text-white"
                                }`}
                            >
                                {n.label}
                            </a>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <a
                            href={BUSINESS.phoneHref}
                            data-testid="header-call-cta"
                            className="pill group inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-600 text-white px-5 py-2.5 text-[14px] font-semibold transition-all shadow-[0_6px_20px_-8px_hsl(var(--brand-red)/0.65)] hover:shadow-[0_10px_24px_-8px_hsl(var(--brand-red)/0.8)]"
                        >
                            <Phone className="h-4 w-4" />
                            {BUSINESS.phone}
                        </a>
                    </div>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className={`lg:hidden p-2 rounded-lg transition-colors ${
                            scrolled || open ? "text-brand-navy" : "text-white"
                        }`}
                        aria-label="Menú"
                    >
                        {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                </div>

                {open && (
                    <div
                        data-testid="mobile-nav"
                        className="lg:hidden pb-5 pt-2 border-t border-black/5"
                    >
                        <div className="flex flex-col gap-1">
                            {NAV.map((n) => (
                                <a
                                    key={n.href}
                                    href={n.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                    className="px-3 py-3 rounded-lg text-brand-ink/85 font-medium hover:bg-black/5"
                                >
                                    {n.label}
                                </a>
                            ))}
                            <a
                                href={BUSINESS.phoneHref}
                                data-testid="mobile-call-cta"
                                className="mt-2 pill inline-flex items-center justify-center gap-2 bg-brand-red text-white px-5 py-3 text-[15px] font-semibold"
                            >
                                <Phone className="h-4 w-4" /> Llamar {BUSINESS.phone}
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
}
